import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLayoutContext } from '../../../Context/Layout.context';
import { Grid, Typography } from '@material-ui/core';
import { Flag } from './Subcomponents/Flag.component';
import { getFlags, updateFlags } from '../../../services/admin/settings/systemSettings.service';
import { FlagStatus, FlagType } from './Subcomponents/Flag.types';
import { useStyles } from '../SystemSettings.styles';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { CancelButton } from '../../buttons/CancelButton.component';
import { ConfirmButton } from '../../buttons/ConfirmButton.component';
import { useTranslation } from 'react-i18next';
import { Paths } from '../../../utils/routerUrls';

export const FlagSetting: React.FC<{
  onClose: () => void;
  onChange: (isChanged: boolean) => void;
  onSave: ()=> void;
}> = ({ onClose, onChange, onSave }) => {
  const [checkedFlags, setCheckedFlags] = useState<FlagStatus[]>([]);
  const [activeFlagIds, setActiveFlagIds] = useState<number[]>([]);
  const [changedFlagIds, setChangedFlagIds] = useState<number[]>([]);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [flags, setFlags] = useState<FlagType[]>([]);
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const { showLoader, openAlert } = useLayoutContext();

  const handleGetSystemFlags = () => {
    getFlags()
      .then((result: FlagType[]) => {
        if (result) {
          setFlags(result);
        }
      })
      .catch(() => {
        history.push(Paths.error);
      });
  };

  const handleOnSave = () => {
    showLoader(true, 'Aktualizuję dane');
    updateFlags(checkedFlags)
      .then(
        () => {
          openAlert('success');
          setIsDataChanged(false);
          onSave();
          setChangedFlagIds([]);
        },
        (error) => {
          if (error.response.data.errors[0].reason === 1) {
            openAlert('error', 'Zapisanie danych nie powiodło się. Przynajmniej jedna flaga musi być włączona');
          }
          openAlert('save_error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const onChangeFlags = (id: number) => {
    setIsDataChanged(true)
    const flagList = checkedFlags;
    const checkedFlag = flagList.find((flag) => flag.id === id);
    if (checkedFlag) {
      if (!checkedFlag.active) {
        checkedFlag.active = true;
        setActiveFlagIds((arr: number[]) => [...arr, id]);
      } else {
        checkedFlag.active = false;
        setActiveFlagIds(activeFlagIds.filter((item: number) => item !== id));
      }
      setCheckedFlags(flagList);
    }
    if(!changedFlagIds.includes(id)) {
      setChangedFlagIds((arr: number[]) => [...arr, id]);
    } else {
      const changedFlags = changedFlagIds.filter((item: number) => item !== id);
      setIsDataChanged(changedFlags.length > 0);
      setChangedFlagIds(changedFlags);
    }

  };

  useEffect(() => {
    handleGetSystemFlags();
  }, []);

  useEffect(() => {
    if (flags) {
      const flagIds: number[] = [];
      const flagList: FlagStatus[] = flags.map((flag) => {
        if (flag.active) {
          flagIds.push(flag.id);
        }
        return { id: flag.id, active: flag.active };
      });
      setCheckedFlags(flagList);
      setActiveFlagIds(flagIds);
    }
  }, [flags]);

  useEffect(() => {
    onChange(isDataChanged);
  }, [isDataChanged]);

  const getColumn = (startIndex: number, endIndex: number): FlagType[] => {
    return flags.slice(startIndex, endIndex);
  };

  const column1 = getColumn(0, 5);
  const column2 = getColumn(5, 10);

  const handleOnClose = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onClose();
  };

  return (
    <>
      <Grid item xs={12}>
        <div className={classes.listHeader}>
          <div>
            <Typography className={classes.tabTitle}>{t('systemSettings:flagSystem.header')}</Typography>
            <Typography className={classes.tabSubTitle}>{t('systemSettings:flagSystem.subHeader')}</Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={6} style={{ marginTop: '25px' }}>
        {column1 &&
          column1.map((flag) => (
            <Flag
              key={flag.id}
              id={flag.id}
              name={flag.name}
              label={flag.name.charAt(0)}
              color={flag.color}
              onChange={() => onChangeFlags(flag.id)}
              isChecked={activeFlagIds.includes(flag.id)}
            />
          ))}
      </Grid>
      <Grid item xs={6} style={{ marginTop: '25px' }}>
        {column2 &&
          column2.map((flag) => (
            <Flag
              key={flag.id}
              id={flag.id}
              name={flag.name}
              label={flag.name.charAt(0)}
              color={flag.color}
              onChange={() => onChangeFlags(flag.id)}
              isChecked={activeFlagIds.includes(flag.id)}
            />
          ))}
      </Grid>
      <Grid item xs={12} style={{ textAlign: 'right', alignItems: 'end', marginTop: 140 }}>
        <CancelButton onClick={handleOnClose}>Anuluj</CancelButton>
        <ConfirmButton onClick={handleOnSave} disabled={!isDataChanged}>
          Zapisz
        </ConfirmButton>
      </Grid>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onClose} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
